import React, { useCallback, useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { AgentService } from '@/services/agent/AgentService';
import { useClickOutside } from '@/hooks/useClickOutside';
import type { SkillInfo } from '@/types/agent';

type TabKey = 'official' | 'custom' | 'favorite';

interface SkillPickerPanelProps {
  onSelect: (skill: SkillInfo) => void;
  onClose: () => void;
  /** 面板相对锚点按钮的水平对齐方式（侧栏等窄容器用 right，向左展开） */
  align?: 'left' | 'right';
  /** 触发按钮锚点（点击它不算“外部”，避免 toggle 按钮被误判后再次打开） */
  triggerRef?: RefObject<HTMLDivElement | null>;
}

const TABS: { key: TabKey; label: string }[] = [
  { key: 'official', label: '官方' },
  { key: 'custom', label: '自定义' },
  { key: 'favorite', label: '收藏' },
];

/** category 含 custom/自定义/user 视为自定义技能 */
function isCustomSkill(skill: SkillInfo): boolean {
  const c = (skill.category || '').toLowerCase();
  return c.includes('custom') || c.includes('自定义') || c.includes('user');
}

/**
 * 智能体面板 - Skill 按钮弹窗选择窗口
 * 设计稿：首页改版/LanhuProject260824 lanhu_neiyeskill（560x400 弹窗）
 */
export const SkillPickerPanel: React.FC<SkillPickerPanelProps> = ({ onSelect, onClose, align = 'left', triggerRef }) => {
  const [skills, setSkills] = useState<SkillInfo[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState<TabKey>('official');
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [starHoveredId, setStarHoveredId] = useState<string | null>(null);
  const [favoriteIds, setFavoriteIds] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem('bongai_favorite_skills');
      return new Set(raw ? (JSON.parse(raw) as string[]) : []);
    } catch {
      return new Set();
    }
  });
  const [reloadTick, setReloadTick] = useState(0);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  // 点击弹窗外部任意位置关闭（document 级监听，不依赖遮罩层与层叠上下文）
  const handleClose = useCallback(() => onClose(), [onClose]);
  useClickOutside([rootRef, triggerRef], handleClose, true);

  // 收藏/取消收藏（localStorage 持久化）
  const toggleFavorite = (skillId: string) => {
    setFavoriteIds((prev) => {
      const next = new Set(prev);
      if (next.has(skillId)) next.delete(skillId);
      else next.add(skillId);
      try {
        localStorage.setItem('bongai_favorite_skills', JSON.stringify([...next]));
      } catch {}
      return next;
    });
  };

  // 加载技能列表
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      try {
        const list = await AgentService.listSkills();
        if (!cancelled) {
          setSkills(list);
          setError(null);
        }
      } catch (e: any) {
        if (!cancelled) setError(e?.message || '技能列表加载失败');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reloadTick]);

  // Esc 关闭
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const visibleSkills = useMemo(() => {
    let list: SkillInfo[] = skills;
    if (tab === 'custom') list = list.filter(isCustomSkill);
    if (tab === 'favorite') list = list.filter((s) => favoriteIds.has(s.id));
    const kw = search.trim().toLowerCase();
    if (kw) {
      list = list.filter(
        (s) =>
          s.name.toLowerCase().includes(kw) ||
          s.description.toLowerCase().includes(kw) ||
          s.id.toLowerCase().includes(kw)
      );
    }
    return list;
  }, [skills, tab, search, favoriteIds]);

  const emptyText =
    tab === 'favorite'
      ? '暂无收藏技能'
      : tab === 'custom'
        ? '暂无自定义技能'
        : search.trim()
          ? '未找到匹配的技能'
          : '暂无技能';

  return (
    <div
      ref={rootRef}
      className="absolute z-50 flex flex-col overflow-hidden"
      style={{
        bottom: 'calc(100% + 8px)',
        left: align === 'left' ? 0 : undefined,
        right: align === 'right' ? 0 : undefined,
        width: '560px',
        height: '400px',
        backgroundColor: '#141414',
        border: '1px solid #2d2d2d',
        borderRadius: '16px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
      }}
    >
      <style>{`
        .skill-picker-scroll::-webkit-scrollbar { width: 10px; }
        .skill-picker-scroll::-webkit-scrollbar-track { background: rgba(217,217,217,0.05); border-radius: 0 0 0 15px; }
        .skill-picker-scroll::-webkit-scrollbar-thumb { background: rgba(217,217,217,0.65); border-radius: 10px; }
      `}</style>

      {/* 头部：Skill 标题 + 搜索框 */}
      <div
        className="flex items-center justify-between shrink-0"
        style={{ height: '56px', backgroundColor: '#222222', padding: '0 12px' }}
      >
        <div className="flex items-center" style={{ gap: '12px' }}>
          <img src="/icons/skill-picker/skill.svg" alt="" width={16} height={16} style={{ objectFit: 'contain' }} draggable={false} />
          <span
            style={{
              fontSize: '14px',
              color: '#ffffff',
              fontFamily: '"PingFang SC", sans-serif',
              lineHeight: '14px',
            }}
          >
            Skill
          </span>
        </div>
        <div
          className="flex items-center"
          style={{
            width: '240px',
            height: '32px',
            backgroundColor: 'rgba(54,54,54,0.25)',
            borderRadius: '8px',
            padding: '0 8px',
            gap: '8px',
          }}
        >
          <img
            src="/icons/skill-picker/search.svg"
            alt=""
            width={16}
            height={16}
            style={{ objectFit: 'contain', flexShrink: 0 }}
            draggable={false}
          />
          <input
            ref={searchInputRef}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="搜索Skill"
            className="w-full bg-transparent border-none outline-none placeholder:text-white/65"
            style={{
              fontSize: '14px',
              color: '#ffffff',
              fontFamily: '"PingFang SC", sans-serif',
              lineHeight: '16px',
              caretColor: '#ffffff',
              padding: 0,
            }}
          />
        </div>
      </div>

      {/* Tab 导航 + 创建 / 全部Skill */}
      <div className="flex items-center shrink-0" style={{ height: '44px', padding: '6px 12px' }}>
        {TABS.map((t) =>
          t.key === tab ? (
            <button
              key={t.key}
              className="flex items-center justify-center shrink-0"
              style={{
                width: '60px',
                height: '32px',
                backgroundColor: '#2d2d2d',
                borderRadius: '8px',
                border: 'none',
                padding: 0,
              }}
            >
              <span
                style={{
                  fontSize: '14px',
                  color: '#ffffff',
                  fontFamily: '"PingFang SC", sans-serif',
                  lineHeight: '14px',
                }}
              >
                {t.label}
              </span>
            </button>
          ) : (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className="flex items-center justify-center shrink-0 transition-colors"
              style={{ height: '32px', padding: '0 12px', backgroundColor: 'transparent', border: 'none' }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'rgba(45,45,45,0.6)')}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
            >
              <span
                style={{
                  fontSize: '14px',
                  color: '#ffffff',
                  fontFamily: '"PingFang SC", sans-serif',
                  lineHeight: '14px',
                }}
              >
                {t.label}
              </span>
            </button>
          )
        )}

        <div className="flex-1" />

        {/* 创建按钮（创建流程占位） */}
        <div
          className="flex items-center justify-center shrink-0"
          style={{
            width: '72px',
            height: '32px',
            backgroundColor: 'rgba(54,54,54,0.25)',
            borderRadius: '8px',
            border: '1px solid rgba(54,54,54,0.45)',
            gap: '8px',
          }}
        >
          <img
            src="/icons/skill-picker/create.svg"
            alt=""
            width={16}
            height={16}
            style={{ objectFit: 'contain' }}
            draggable={false}
          />
          <span
            style={{
              fontSize: '14px',
              color: '#ffffff',
              fontFamily: '"PingFang SC", sans-serif',
              lineHeight: '14px',
            }}
          >
            创建
          </span>
        </div>

        {/* 全部Skill：重置搜索与分类 */}
        <button
          onClick={() => {
            setSearch('');
            setTab('official');
          }}
          className="flex items-center justify-center shrink-0 transition-colors"
          style={{
            width: '80px',
            height: '32px',
            backgroundColor: 'rgba(54,54,54,0.25)',
            borderRadius: '8px',
            border: '1px solid rgba(54,54,54,0.45)',
            marginLeft: '8px',
            padding: 0,
          }}
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'rgba(54,54,54,0.45)')}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'rgba(54,54,54,0.25)')}
        >
          <span
            style={{
              fontSize: '14px',
              color: '#ffffff',
              fontFamily: '"PingFang SC", sans-serif',
              lineHeight: '14px',
            }}
          >
            全部Skill
          </span>
        </button>
      </div>

      {/* 技能列表 */}
      {isLoading ? (
        <div className="flex-1 flex items-center justify-center">
          <span style={{ fontSize: '14px', color: 'rgba(255,255,255,0.45)', fontFamily: '"PingFang SC", sans-serif' }}>
            加载中...
          </span>
        </div>
      ) : error ? (
        <div className="flex-1 flex flex-col items-center justify-center" style={{ gap: '12px' }}>
          <span style={{ fontSize: '14px', color: 'rgba(255,255,255,0.65)', fontFamily: '"PingFang SC", sans-serif' }}>
            {error}
          </span>
          <button
            onClick={() => setReloadTick((v) => v + 1)}
            className="flex items-center justify-center"
            style={{
              width: '72px',
              height: '32px',
              backgroundColor: '#2d2d2d',
              borderRadius: '8px',
              border: 'none',
              padding: 0,
            }}
          >
            <span style={{ fontSize: '14px', color: '#ffffff', fontFamily: '"PingFang SC", sans-serif' }}>重试</span>
          </button>
        </div>
      ) : visibleSkills.length === 0 ? (
        <div className="flex-1 flex items-center justify-center">
          <span style={{ fontSize: '14px', color: 'rgba(255,255,255,0.45)', fontFamily: '"PingFang SC", sans-serif' }}>
            {emptyText}
          </span>
        </div>
      ) : (
        <div className="skill-picker-scroll flex-1 overflow-y-auto flex flex-col" style={{ padding: '4px 12px 12px', gap: '8px' }}>
          {visibleSkills.map((s) => (
            <button
              key={s.id}
              onClick={() => onSelect(s)}
              onMouseEnter={() => setHoveredId(s.id)}
              onMouseLeave={() => setHoveredId(null)}
              className="w-full flex items-center text-left shrink-0"
              style={{
                height: '64px',
                backgroundColor: hoveredId === s.id ? '#2d2d2d' : 'transparent',
                borderRadius: '16px',
                border: 'none',
                padding: '0 16px',
                gap: '16px',
                transition: 'background-color 0.15s ease',
              }}
            >
              {/* 收藏星星（设计稿 icon_SC）：默认描边，悬停/已收藏切换实心黄色版 */}
              <img
                src={
                  favoriteIds.has(s.id) || starHoveredId === s.id
                    ? '/icons/skill-picker/star-filled.svg'
                    : '/icons/skill-picker/star-outline.svg'
                }
                alt={favoriteIds.has(s.id) ? '取消收藏' : '收藏'}
                role="button"
                width={16}
                height={16}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleFavorite(s.id);
                }}
                onMouseEnter={() => setStarHoveredId(s.id)}
                onMouseLeave={() => setStarHoveredId(null)}
                style={{ objectFit: 'contain', flexShrink: 0, cursor: 'pointer' }}
                draggable={false}
              />
              <div className="flex-1 min-w-0 flex flex-col">
                <span
                  style={{
                    fontSize: '14px',
                    color: '#ffffff',
                    fontFamily: '"PingFang SC", sans-serif',
                    lineHeight: '14px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {s.name}
                </span>
                <span
                  style={{
                    fontSize: '12px',
                    color: 'rgba(255,255,255,0.45)',
                    fontFamily: '"PingFang SC", sans-serif',
                    lineHeight: '12px',
                    marginTop: '8px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {s.description}
                </span>
              </div>
              <div
                className="shrink-0 flex items-center"
                style={{ backgroundColor: '#1d1d1d', borderRadius: '2px', height: '16px', padding: '0 4px', maxWidth: '200px' }}
              >
                <span
                  style={{
                    fontSize: '12px',
                    color: 'rgba(255,255,255,0.45)',
                    fontFamily: '"PingFang SC", sans-serif',
                    lineHeight: '14px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  /{s.id}
                </span>
              </div>
              <div
                className="shrink-0 flex items-center justify-center"
                style={{
                  width: '60px',
                  height: '32px',
                  backgroundColor: '#1d1d1d',
                  borderRadius: '8px',
                  marginLeft: '12px',
                  opacity: hoveredId === s.id ? 1 : 0,
                  transition: 'opacity 0.15s ease',
                }}
              >
                <span
                  style={{
                    fontSize: '14px',
                    color: '#ffffff',
                    fontFamily: '"PingFang SC", sans-serif',
                    lineHeight: '14px',
                  }}
                >
                  查看
                </span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
